import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { getAllMeetings, MEETING_ALL_GET } from "@/api";
import { Meeting } from "@/types";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { useMemo, useCallback, useState, useEffect } from "react";
import { useWebSocket } from "@/context/WebSocketProvider";
import { CloudEventType, CloudEvent } from "@/constants/websocket-events";

const FETCH_SIZE = 20;

export interface SelectableMeetingsOptions {
  /** Days of history to include before today. 0 (the default) is today only. */
  lookbackDays?: number;
  /** Server-side title match. Empty string means no filtering. */
  search?: string;
}

/**
 * Sort meetings by proximity to current time:
 * 1. In-progress meetings first (startAt <= now <= endAt)
 * 2. Future meetings (sorted by startAt ascending)
 * 3. Past meetings (sorted by endAt descending - most recent first)
 */
const sortMeetingsByProximity = (meetings: Meeting[]): Meeting[] => {
  const now = Date.now();

  const inProgress: Meeting[] = [];
  const future: Meeting[] = [];
  const past: Meeting[] = [];

  meetings.forEach((meeting) => {
    if (meeting.startAt <= now && meeting.endAt >= now) {
      inProgress.push(meeting);
    } else if (meeting.startAt > now) {
      future.push(meeting);
    } else {
      past.push(meeting);
    }
  });

  // Sort future meetings by start time (earliest first)
  future.sort((a, b) => a.startAt - b.startAt);

  // Sort past meetings by end time (most recent first)
  past.sort((a, b) => b.endAt - a.endAt);

  // Sort in-progress meetings by start time
  inProgress.sort((a, b) => a.startAt - b.startAt);

  return [...inProgress, ...future, ...past];
};

/**
 * Calendar events the user can attach a recording to: today by default, or the
 * last `lookbackDays` days as well, optionally narrowed by a title search the
 * server runs.
 */
export const useSelectableMeetings = (
  enabled: boolean = true,
  { lookbackDays = 0, search = "" }: SelectableMeetingsOptions = {},
) => {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  // Use state to track today's date, allowing it to update at midnight
  const [today, setToday] = useState(() => new Date());

  // Update today's date at midnight to avoid stale date issues
  useEffect(() => {
    const now = new Date();
    const msUntilMidnight = endOfDay(now).getTime() - now.getTime() + 1; // +1ms to be in the new day

    const timeoutId = setTimeout(() => {
      setToday(new Date());
    }, msUntilMidnight);

    return () => clearTimeout(timeoutId);
  }, [today]); // Re-schedule when today changes

  const startAt = useMemo(
    () => startOfDay(subDays(today, lookbackDays)).getTime(),
    [today, lookbackDays],
  );
  const endAt = useMemo(() => endOfDay(today).getTime(), [today]);

  // Subscribe to meeting events via WebSocket CloudEvents
  useEffect(() => {
    if (!enabled) return;

    const handleMessage = (event: CloudEvent) => {
      // Handle all meeting events by invalidating the query
      if (
        event.type === CloudEventType.MEETING_CREATED ||
        event.type === CloudEventType.MEETING_UPDATED ||
        event.type === CloudEventType.MEETING_DELETED ||
        event.type === CloudEventType.MEETING_STATUS_UPDATED
      ) {
        queryClient.invalidateQueries({
          queryKey: [MEETING_ALL_GET],
        });
      }
    };

    // Subscribe to all meeting events (no specific entityId)
    const unsubscribe = subscribe("meeting", undefined, handleMessage);

    return () => {
      unsubscribe();
    };
  }, [enabled, subscribe, queryClient]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isFetched,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: [MEETING_ALL_GET, "selectable-meetings", startAt, endAt, search],
    queryFn: async ({ pageParam = 0 }) => {
      const skip = pageParam * FETCH_SIZE;
      const meetings = await getAllMeetings({
        startAt,
        endAt,
        skip,
        take: FETCH_SIZE,
        includeInvitees: true,
        search: search || undefined,
        // Without an explicit order the server leaves `orderBy` unset, so
        // skip/take paging over a multi-day window can repeat or drop rows.
        asc: false,
      });
      return meetings;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If we got fewer results than requested, there are no more pages
      if (lastPage.length < FETCH_SIZE) {
        return undefined;
      }
      return allPages.length;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Flatten pages and deduplicate by id
  const allMeetings = useMemo(() => {
    if (!data?.pages) return [];

    const meetingsMap = new Map<string, Meeting>();
    data.pages.flat().forEach((meeting) => {
      meetingsMap.set(meeting.id, meeting);
    });

    return Array.from(meetingsMap.values());
  }, [data?.pages]);

  // Sort meetings by proximity to current time
  const sortedMeetings = useMemo(
    () => sortMeetingsByProximity(allMeetings),
    [allMeetings],
  );

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    meetings: sortedMeetings,
    loadMore,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isFetched,
    isError,
    refetch,
  };
};
